import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { LANG_PREFIXES, stripLangPrefix } from './LangWrapper';
// The origin of the platform this bundle IS — todae.net on the shop build.
// Not BRAND.siteUrl, which is always the planner's.
import { SITE_ORIGIN as SITE } from '../config/platform';

/** All languages including English */
const ALL_LANGS = ['en', ...LANG_PREFIXES] as const;

/** Convert internal lang code to hreflang attribute value */
function toHreflang(lang: string): string {
  if (lang === 'pt-pt') return 'pt-PT';
  return lang;
}

// og:locale territories chosen for the US + Africa market: es→US Hispanic,
// ar→Egypt (North Africa), fr→francophone Africa, pt→lusophone Africa, af/zu→ZA.
const ogLocaleMap: Record<string, string> = {
  en: 'en_US', es: 'es_US', fr: 'fr_FR', ar: 'ar_EG',
  'pt-pt': 'pt_PT', af: 'af_ZA', zu: 'zu_ZA',
};

/**
 * Renders <Helmet> with hreflang, canonical, og:locale, and html lang
 * for the current page across all supported languages.
 *
 * Uses react-helmet-async so tags are captured during SSG (not useEffect).
 * Placed in RootLayout so it runs on every page.
 */
export default function HreflangTags() {
  const location = useLocation();
  // Strip lang prefix AND trailing slashes so all URLs are normalized
  // (matches the post-process canonical format)
  const basePath = stripLangPrefix(location.pathname).replace(/\/+$/, '');

  // Don't add hreflang for protected/non-translatable routes.
  // Match on exact path or "prefix + /" so "/cctv-design" does NOT also
  // skip sibling SEO landings like "/cctv-design-tool" / "-software".
  // /compare/sku/ is EN-only programmatic SKU comparison (Day-3 sprint) — no per-lang
  // routes exist, so emitting hreflang would point Google at 404s.
  // /nvr/ is EN-only too (route is the bare `nvr/:slug`, canonical points at the EN
  // URL, content is English). Without this skip, HreflangTags emitted 22 cross-language
  // alternates per NVR (e.g. /ja/nvr/<model>) that have no route → SPA-fallback NotFound
  // → noindex, which Google reported under "Excluded by 'noindex' tag".
  // /p/ (per-customer Portal share links) and /unsubscribed (email utility) are
  // private/non-SEO and have no per-language routes — never emit hreflang for them.
  const skipPrefixes = ['/auth', '/dashboard', '/cctv-design', '/checkout-success', '/api-docs', '/camera/', '/nvr/', '/compare/sku/', '/p/', '/unsubscribed'];
  if (skipPrefixes.some(p => basePath === p || basePath.startsWith(p.endsWith('/') ? p : p + '/'))) {
    return null;
  }

  // Detect current language from URL (handles both /es and /es/)
  const currentLangPrefix = LANG_PREFIXES.find(
    p => location.pathname.startsWith(`/${p}/`) || location.pathname === `/${p}` || location.pathname === `/${p}/`
  );
  const currentLang = currentLangPrefix || 'en';

  // Language-prefixed routes exist ONLY for /docs (src/routes.tsx, 24→7 i18n
  // cut). Every other page is English-only at the URL level: a full
  // cross-language set would advertise /{lang}/<path> URLs that 404 →
  // SPA-fallback NotFound → noindex (the documented GSC failure class).
  // So: docs pages get the full alternate set; everything else self-only.
  const docsBase = basePath === '/docs' || basePath.startsWith('/docs/');
  const isLocalizedSinglePage = !docsBase;

  // Build hreflang entries (no trailing slash, consistent with canonical).
  // Single-language pages get a self-reference only; cross-language pages
  // get the full ALL_LANGS set.
  const hreflangs = isLocalizedSinglePage
    ? [{
        hreflang: toHreflang(currentLang),
        href: currentLang === 'en'
          ? (basePath ? `${SITE}${basePath}` : SITE)
          : `${SITE}/${currentLang}${basePath}`,
      }]
    : ALL_LANGS.map(lang => ({
        hreflang: toHreflang(lang),
        href: lang === 'en'
          ? (basePath ? `${SITE}${basePath}` : SITE)
          : `${SITE}/${lang}${basePath}`,
      }));

  // Self-canonical: every page (including localized variants) points to
  // itself so Google indexes each language version instead of treating
  // non-English URLs as duplicates of the English root.
  const canonicalHref = currentLang === 'en'
    ? (basePath ? `${SITE}${basePath}` : SITE)
    : `${SITE}/${currentLang}${basePath}`;

  return (
    <Helmet>
      {/* BCP-47 compliant html lang — pt-br → pt-BR, pt-pt → pt-PT, rest unchanged */}
      <html lang={toHreflang(currentLang)} dir={currentLang === 'ar' ? 'rtl' : 'ltr'} />
        <meta property="og:locale" content={ogLocaleMap[currentLang] || 'en_US'} />
      <link rel="canonical" href={canonicalHref} />
      <meta property="og:url" content={canonicalHref} />
      {/* Hreflang alternates — full set for translatable pages, self-ref only
          for English-only single-language pages. */}
      {hreflangs.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}
      {/* x-default + og:locale:alternate only for pages with real cross-language
          alternates. English-only single-language pages don't have a "default" —
          the one URL that exists is both default and the only language version. */}
      {!isLocalizedSinglePage && (
        <link rel="alternate" hrefLang="x-default" href={basePath ? `${SITE}${basePath}` : SITE} />
      )}
      {!isLocalizedSinglePage && ALL_LANGS.filter(l => l !== currentLang).map(lang => (
        <meta key={lang} property="og:locale:alternate" content={ogLocaleMap[lang] || lang} />
      ))}
    </Helmet>
  );
}
